import cx from 'classnames';
import type {
  ForwardedRef,
  HTMLAttributes,
  MouseEventHandler,
  PropsWithChildren,
  ReactElement,
} from 'react';
import { forwardRef } from 'react';

import { Button } from '@trello/nachos/button';
import { RouterLink } from '@trello/router/router-link';
import type { QuickCardEditorTestIds } from '@trello/test-ids';

import styles from './QuickCardEditorButton.less';

interface QuickCardEditorButtonProps
  extends HTMLAttributes<HTMLAnchorElement | HTMLButtonElement> {
  icon: ReactElement;
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  href?: string;
  testId?: QuickCardEditorTestIds;
  /**
   * Temporary prop in order to maintain style parity with the old stack.
   * When `QuickCardEditorView` is deleted, we can remove this prop.
   * @default false
   */
  isLegacyView?: boolean;
  isSelected?: boolean;
}

export const QuickCardEditorButton = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  PropsWithChildren<QuickCardEditorButtonProps>
>(
  (
    { children, href, icon, isLegacyView = false, isSelected, ...props },
    ref,
  ) => {
    const className = cx(
      styles.button,
      isLegacyView && styles['button--legacy'],
      !isSelected && styles['button--hover'],
    );

    if (href) {
      return (
        <RouterLink
          className={className}
          href={href}
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          {...props}
        >
          <span className={styles.icon}>{icon}</span>
          {children}
        </RouterLink>
      );
    }

    return (
      <Button
        className={className}
        iconBefore={icon}
        ref={ref as ForwardedRef<HTMLButtonElement>}
        isSelected={isSelected}
        {...props}
      >
        {children}
      </Button>
    );
  },
);
